import { privateApiClient } from './apiClient';

// pos-service master-dev : /pos/clients (l'ancien /pos/customers n'existe plus)
const CUSTOMERS_API = '/pos/clients';

const CLIENT_TYPE_CODES = new Set(['PP', 'PM', 'PC', 'PL', 'AO']);

/**
 * Affichage court du type client : toujours [PP], [PM], [PC], [PL] ou [AO] selon le backend.
 * Accepte client_type / clientType / type ou une chaîne seule.
 */
export function clientTypeBracket(clientOrType) {
    const raw =
        typeof clientOrType === 'string'
            ? clientOrType
            : (clientOrType?.client_type ?? clientOrType?.clientType ?? clientOrType?.type ?? 'PP');
    let s = String(raw ?? 'PP').trim();
    const inBrackets = s.match(/\[(PP|PM|PC|PL|AO)\]/i);
    if (inBrackets) return `[${inBrackets[1].toUpperCase()}]`;
    const asWord = s.match(/\b(PP|PM|PC|PL|AO)\b/i);
    if (asWord) return `[${asWord[1].toUpperCase()}]`;
    const compact = s.replace(/[\s._-]/g, '').toUpperCase();
    for (const code of CLIENT_TYPE_CODES) {
        if (compact === code || compact.startsWith(code)) return `[${code}]`;
    }
    return '[PP]';
}

/**
 * Normalise un client du pos-service (ClientRspDTO).
 * `clientType` y est un objet { id, type, description… } : l'UI n'utilise que le code (PP, PM…),
 * l'id est gardé à part car c'est lui que l'API attend en écriture.
 */
const normalizeCustomer = (raw) => {
    if (!raw || typeof raw !== 'object') return raw;
    const companyId = raw.company_id ?? raw.companyId ?? null;
    const typeObj = raw.clientType && typeof raw.clientType === 'object' ? raw.clientType : null;
    const typeCode = typeObj?.type ?? typeObj?.code
        ?? (typeof raw.client_type === 'string' ? raw.client_type : null)
        ?? (typeof raw.clientType === 'string' ? raw.clientType : null)
        ?? (typeof raw.type === 'string' ? raw.type : null)
        ?? 'PP';
    return {
        ...raw,
        company_id: companyId,
        companyId,
        client_type: typeCode,
        clientType: typeCode,
        client_type_id: typeObj?.id ?? raw.client_type_id ?? null,
        clientTypeDescription: typeObj?.description ?? raw.typeDesc ?? null,
    };
};

let clientTypesCache = null;

/** L'API attend l'id du type de client : convertit un code (PP, PM…) en id via /pos/client-types. */
const resolveClientTypeId = async (customerData) => {
    if (customerData.client_type_id) return customerData.client_type_id;
    const value = customerData.client_type ?? customerData.clientType ?? 'PP';
    const code = typeof value === 'object' ? (value.id ?? value.type) : value;
    if (!CLIENT_TYPE_CODES.has(String(code).toUpperCase())) return code;
    if (!clientTypesCache) {
        clientTypesCache = (await fetchClientTypes()).contents || [];
    }
    const match = clientTypesCache.find((t) => String(t.type ?? t.code ?? '').toUpperCase() === String(code).toUpperCase());
    return match?.id ?? code;
};

/** Payload ClientRqsDTO (pos-service master-dev). */
const buildCustomerWritePayload = async (customerData) => ({
    id: customerData.id ?? null,
    zip: customerData.zip ?? '243',
    phoneNumber: String(customerData.phoneNumber ?? '').replace(/\s+/g, ''),
    email: customerData.email ?? '',
    name: customerData.name?.trim() ?? '',
    nif: customerData.nif ?? null,
    docReference: customerData.docReference ?? null,
    client_type: await resolveClientTypeId(customerData),
    showInfoOnInvoice: customerData.showInfoOnInvoice ?? true,
    address: customerData.address ?? '',
    company_id: customerData.company_id ?? customerData.companyId ?? null,
});

/**
 * Service pour gérer les clients (API : /pos/clients).
 */

/**
 * Récupérer la liste des types de client (GET /pos/client-types si disponible).
 */
export const fetchClientTypes = async (params = {}) => {
    const defaultParams = {
        pge: 1,
        lmt: 50,
        slg: '',
        ...params
    };

    try {
        const { data } = await privateApiClient.get('/pos/client-types', {
            params: defaultParams
        });

        if (data?.contents && Array.isArray(data.contents)) {
            return { contents: data.contents };
        }

        return { contents: [] };
    } catch (error) {
        if (error?.response?.status === 404) {
            console.warn('[customerService] Endpoint /pos/client-types non disponible (404)');
            return { contents: [] };
        }

        console.error('[customerService] Erreur lors de la récupération des types de client:', error);
        return { contents: [] };
    }
};

/**
 * Récupérer un client par ID (GET /pos/clients/{id})
 */
export const fetchCustomerById = async (customerId) => {
    const { data } = await privateApiClient.get(`${CUSTOMERS_API}/${customerId}`);
    if (data?.error) throw new Error(data.message || 'Client non trouvé');
    return normalizeCustomer(data?.payload ?? data?.contents ?? data);
};

/**
 * Récupérer la liste des clients (GET /pos/clients?org=&pge=&lmt=&slg=)
 */
export const fetchCustomers = async (params = {}) => {
    const defaultParams = {
        pge: 1,
        lmt: 10,
        slg: '',
        ...params
    };

    try {
        const { data } = await privateApiClient.get(CUSTOMERS_API, {
            params: defaultParams
        });

        if (data?.contents) {
            return {
                contents: (data.contents || []).map(normalizeCustomer),
                nextPage: data.nextPage || null,
                previousPage: data.previousPage || null
            };
        }

        return {
            contents: [],
            nextPage: null,
            previousPage: null
        };
    } catch (error) {
        if (error?.response?.status === 404) {
            console.warn('[customerService] Endpoint /pos/clients non disponible (404)');
            return {
                contents: [],
                nextPage: null,
                previousPage: null
            };
        }

        console.error('[customerService] Erreur lors de la récupération des clients:', error);
        throw error;
    }
};

/**
 * Créer un nouveau client (POST /pos/clients)
 */
export const createCustomer = async (customerData) => {
    const companyId = customerData.company_id ?? customerData.companyId;
    if (!companyId) {
        throw new Error('company_id est requis pour créer un client');
    }
    if (!customerData.name?.trim()) {
        throw new Error('Le nom du client est requis');
    }
    if (!customerData.phoneNumber?.trim()) {
        throw new Error('Le numéro de téléphone est requis');
    }

    const payload = await buildCustomerWritePayload({ ...customerData, company_id: companyId });

    try {
        const response = await privateApiClient.post(CUSTOMERS_API, payload);
        const data = response?.data;

        if (!data) {
            throw new Error('Aucune réponse de l\'API');
        }

        if (data.error) {
            throw new Error(data.message || 'Erreur lors de la création du client');
        }

        return normalizeCustomer(data.payload ?? data);
    } catch (error) {
        console.error('[customerService] Erreur lors de la création:', error);

        if (error?.response) {
            const errorData = error.response.data;
            if (errorData?.message) throw new Error(errorData.message);
            if (typeof errorData?.error === 'string') throw new Error(errorData.error);
            if (error.response.status === 404) {
                throw new Error('Endpoint clients non disponible sur le serveur (/pos/clients).');
            }
        }

        throw error instanceof Error ? error : new Error(error?.message || 'Erreur lors de la création du client');
    }
};

/**
 * Mettre à jour un client (PUT /pos/clients/{id})
 */
export const updateCustomer = async (customerId, customerData) => {
    const payload = await buildCustomerWritePayload(customerData);
    const { data } = await privateApiClient.put(`${CUSTOMERS_API}/${customerId}`, payload);

    if (data?.error) {
        throw new Error(data.message || 'Erreur lors de la mise à jour du client');
    }

    return normalizeCustomer(data?.payload ?? data?.contents ?? data);
};

/**
 * Supprimer un client (GET /pos/clients/{id}/delete)
 */
export const deleteCustomer = async (customerId) => {
    // Un client avec historique est fusionné dans sa fiche en double, ou refusé avec un message explicite
    let data;
    try {
        ({ data } = await privateApiClient.get(`${CUSTOMERS_API}/${customerId}/delete`));
    } catch (error) {
        throw new Error(error?.response?.data?.message || error.message || 'Erreur lors de la suppression du client');
    }

    if (data?.error) {
        throw new Error(data.message || 'Erreur lors de la suppression du client');
    }

    return data || { message: 'Client supprimé' };
};
